import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import { fetchApi } from '../api/client';
import { useAuth } from '../hooks/useAuth';

// uiLang is what the user selects (including EN).
// lang is the DB language sent to API calls — EN maps to DE since data is only in DE/FR/IT.
export type UILang = 'EN' | 'DE' | 'FR' | 'IT';
export type DBLang = 'DE' | 'FR' | 'IT';

const VALID_UI_LANGS: readonly UILang[] = ['EN', 'DE', 'FR', 'IT'];
const DEFAULT_UI_LANG: UILang = 'DE';

function readStoredUiLang(): UILang | null {
  const raw = localStorage.getItem('ui_lang') as UILang | null;
  return raw && VALID_UI_LANGS.includes(raw) ? raw : null;
}

// Sync the global dayjs locale so every `dayjs(...).format(...)` (month/weekday
// names in session dates, calendar labels, etc.) matches the UI language.
// EN → dayjs built-in 'en'; DE/FR/IT locales are imported above. Set
// synchronously (not only in an effect) so the render that follows a language
// switch already formats with the new locale instead of lagging one render.
function applyDayjsLocale(l: UILang) {
  dayjs.locale(l === 'EN' ? 'en' : l.toLowerCase());
}
// Apply once at module load from the stored/default language.
applyDayjsLocale(readStoredUiLang() ?? DEFAULT_UI_LANG);

export function toDbLang(ui: UILang): DBLang {
  return ui === 'EN' ? 'DE' : ui;
}

export interface AppContextType {
  uiLang: UILang;
  setUiLang: (l: UILang) => void;
  lang: DBLang;       // derived from uiLang — always a real DB language, safe to pass to APIs
  parties: any[];
  factions: any[];
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  // Initial uiLang priority (synchronous, before render):
  //   1. localStorage 'ui_lang' — set on a previous Settings change (same
  //      browser) or by the server-side hydration effect below on a prior
  //      session in this browser.
  //   2. DEFAULT_UI_LANG ('DE') — fallback for a fresh browser / first
  //      visit. Previously defaulted to 'EN'; switched to DE because the
  //      product is a Swiss parliament tool whose audience is primarily
  //      German-speaking, and the data labels themselves are DE-native.
  //
  // For signed-in users, the effect below then overrides with the
  // user's saved server-side preference (so a different browser / a
  // post-session-timeout reload still restores their chosen language).
  const [uiLang, setUiLangState] = useState<UILang>(
    () => readStoredUiLang() ?? DEFAULT_UI_LANG,
  );

  const setUiLang = (l: UILang) => {
    localStorage.setItem('ui_lang', l);
    applyDayjsLocale(l);
    setUiLangState(l);
  };
  const lang: DBLang = toDbLang(uiLang);

  // Server-side hydration. When the Clerk session resolves with a user id,
  // pull /v1/settings/{id} and apply the saved `ui_lang` if present. This
  // is what makes the preference survive (a) a different browser with no
  // localStorage, and (b) a Clerk session timeout where Settings.tsx may
  // not have mounted yet on the next login. Settings.tsx still runs its
  // own copy of this fetch (it also reads other user-prefs), but doing it
  // here too means the UI applies the saved language immediately on every
  // page, not only after the Settings tab is opened.
  const { user } = useAuth();
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    fetchApi<{ ui_lang?: string; is_new?: boolean }>(`/v1/settings/${user.id}`)
      .then(s => {
        if (cancelled || s.is_new) return;
        const serverLang = s.ui_lang as UILang | undefined;
        if (serverLang && VALID_UI_LANGS.includes(serverLang) && serverLang !== uiLang) {
          localStorage.setItem('ui_lang', serverLang);
          applyDayjsLocale(serverLang);
          setUiLangState(serverLang);
        }
      })
      .catch(() => { /* ignore — keep current uiLang */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const [parties, setParties]   = useState<any[]>([]);
  const [factions, setFactions] = useState<any[]>([]);

  useEffect(() => {
    fetchApi<any>('/v1/parties', { active_only: true, top: 200, language: lang })
      .then(res => {
        setParties(res.parties.results || []);
        setFactions(res.factions.results || []);
      })
      .catch(err => console.error("Failed to fetch parties", err));
  }, [lang]);

  return (
    <AppContext.Provider value={{ uiLang, setUiLang, lang, parties, factions }}>
      {children}
    </AppContext.Provider>
  );
}

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext outside AppProvider");
  return ctx;
};
